import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import Table, { Column } from 'ui/Table';
import StatCard from 'ui/StatCard';
import Loading from 'ui/Loading';
import Modal from 'ui/Modal';
import Input from 'ui/Input';
import Button from 'ui/Button';
import ImageUploader, { ImageFile } from 'ui/ImageUploader';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import StatsGrid from 'ui/StatsGrid';
import FilterSection from 'ui/FilterSection';
import TableContainer from 'ui/TableContainer';
import SearchInput from 'ui/SearchInput';
import { awardApi, Award, CreateAwardDto, UpdateAwardDto } from 'apis/award';
import { toast } from 'react-toastify';

interface AwardForm {
    award_name: string;
    issuing_organization: string;
    award_number: string;
    award_date: string;
    photo_images: ImageFile[];
}

const AwardManagementPage: React.FC = () => {
    const [awards, setAwards] = useState<Award[]>([]);
    const [filteredAwards, setFilteredAwards] = useState<Award[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingAward, setEditingAward] = useState<Award | null>(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    const [awardForm, setAwardForm] = useState<AwardForm>({
        award_name: '',
        issuing_organization: '',
        award_number: '',
        award_date: '',
        photo_images: []
    });

    const [searchParams, setSearchParams] = useSearchParams();

    // Fetch awards from API
    useEffect(() => {
        fetchAwards();
    }, []);

    // ?edit=<id> 쿼리 파람으로 진입 시 해당 상훈 수정 모달 자동 열기
    useEffect(() => {
        const editId = searchParams.get('edit');
        if (!editId || awards.length === 0) return;
        const target = awards.find(a => a.id === Number(editId));
        if (target) {
            handleEdit(target);
            searchParams.delete('edit');
            setSearchParams(searchParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [awards, searchParams]);

    const fetchAwards = async () => {
        try {
            setLoading(true);
            const response = await awardApi.getAwards({ limit: 1000 });
            setAwards(response.data.data);
            setFilteredAwards(response.data.data);
        } catch (error) {
            console.error('[Award] Failed to fetch awards:', error);
            toast.error('상훈 정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    // 검색 및 필터링
    useEffect(() => {
        let filtered = awards;

        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(award =>
                award.award_name.toLowerCase().includes(lowerSearch) ||
                award.issuing_organization.toLowerCase().includes(lowerSearch) ||
                (award.award_number && award.award_number.toLowerCase().includes(lowerSearch))
            );
        }

        // Sort by award date (newest first)
        filtered.sort((a, b) => {
            if (!a.award_date) return 1;
            if (!b.award_date) return -1;
            return new Date(b.award_date).getTime() - new Date(a.award_date).getTime();
        });

        setFilteredAwards(filtered);
    }, [awards, searchTerm]);

    const handleCreate = () => {
        setEditingAward(null);
        setAwardForm({
            award_name: '',
            issuing_organization: '',
            award_number: '',
            award_date: '',
            photo_images: []
        });
        setShowCreateModal(true);
    };

    const handleEdit = (award: Award) => {
        setEditingAward(award);
        setAwardForm({
            award_name: award.award_name,
            issuing_organization: award.issuing_organization,
            award_number: award.award_number || '',
            award_date: award.award_date ? award.award_date.split('T')[0] : '',
            photo_images: award.photo_url ? [{
                id: '1',
                url: award.photo_url,
                name: 'photo.jpg'
            }] : []
        });
        setShowCreateModal(true);
    };

    const handleImagesChange = (images: ImageFile[]) => {
        setAwardForm({ ...awardForm, photo_images: images });
    };

    const handleSave = async () => {
        try {
            setUploading(true);

            let photoUrl = '';

            // Upload photo if provided
            if (awardForm.photo_images.length > 0) {
                const image = awardForm.photo_images[0];
                if (image.file) {
                    try {
                        photoUrl = await awardApi.uploadPhoto(image.file);
                    } catch (uploadError) {
                        console.error('[Award] Failed to upload photo:', uploadError);
                        toast.error('사진 업로드에 실패했습니다.');
                        setUploading(false);
                        return;
                    }
                } else if (image.url) {
                    photoUrl = image.url;
                }
            }

            const awardData = {
                award_name: awardForm.award_name,
                issuing_organization: awardForm.issuing_organization,
                award_number: awardForm.award_number || undefined,
                award_date: awardForm.award_date,
                photo_url: photoUrl || undefined
            };

            if (editingAward) {
                // Update existing award
                await awardApi.updateAward(editingAward.id, awardData as UpdateAwardDto);
                toast.success('상훈 정보가 수정되었습니다.');
            } else {
                // Create new award
                await awardApi.createAward(awardData as CreateAwardDto);
                toast.success('상훈이 등록되었습니다.');
            }

            setShowCreateModal(false);
            fetchAwards(); // Refresh the list
        } catch (error) {
            console.error('[Award] Failed to save award:', error);
            toast.error('상훈 저장에 실패했습니다.');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (awardId: number) => {
        if (window.confirm('이 상훈 정보를 삭제하시겠습니까?')) {
            try {
                await awardApi.deleteAward(awardId);
                toast.success('상훈이 삭제되었습니다.');
                fetchAwards(); // Refresh the list
            } catch (error) {
                console.error('[Award] Failed to delete award:', error);
                toast.error('상훈 삭제에 실패했습니다.');
            }
        }
    };


    const getStatistics = () => {
        const total = awards.length;
        const currentYear = new Date().getFullYear();
        const thisYear = awards.filter(a =>
            a.award_date && new Date(a.award_date).getFullYear() === currentYear
        ).length;

        // Get unique organizations
        const organizations = new Set(awards.map(a => a.issuing_organization));
        const uniqueOrgs = organizations.size;

        return { total, thisYear, uniqueOrgs };
    };

    const stats = getStatistics();

    const formatDate = (dateString?: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const columns: Column<Award>[] = [
        {
            key: 'id',
            header: '번호',
            width: '64px',
            align: 'center',
            sortable: true,
            render: (value) => <span className="tabular-nums text-muted-foreground">{value}</span>
        },
        {
            key: 'award_name',
            header: '상훈명',
            sortable: true,
            render: (value) => <span className="break-keep">{value}</span>
        },
        {
            key: 'issuing_organization',
            header: '수여기관',
            width: '200px',
            sortable: true,
            render: (value) => <span className="break-keep">{value}</span>
        },
        {
            key: 'award_number',
            header: '번호',
            width: '120px',
            align: 'center',
            render: (value) => value ? <span className="whitespace-nowrap tabular-nums">{value}</span> : '-'
        },
        {
            key: 'award_date',
            header: '수여일',
            width: '120px',
            align: 'center',
            sortable: true,
            render: (value) => <span className="whitespace-nowrap tabular-nums">{formatDate(value)}</span>
        },
        {
            key: 'photo_url',
            header: '사진',
            width: '72px',
            align: 'center',
            render: (value) => value ? (
                <img
                    src={value}
                    alt="상훈 사진"
                    className="mx-auto h-10 w-10 cursor-pointer rounded border border-border object-cover"
                    onClick={(e) => {
                        e.stopPropagation();
                        window.open(value, '_blank');
                    }}
                />
            ) : '-'
        }
    ];

    if (loading) {
        return (
            <PageContainer>
                <Loading text="상훈 정보를 불러오는 중..." />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="상훈 관리"
                subtitle="수상 및 표창 현황 관리"
                actions={
                    <Button variant="primary" icon={<Plus />} onClick={handleCreate}>
                        상훈 등록
                    </Button>
                }
            />

            <StatsGrid columns={3} className="mb-4">
                <StatCard
                    title="총 상훈수"
                    value={stats.total}
                    suffix="건"
                />

                <StatCard
                    title="올해 수상"
                    value={stats.thisYear}
                    suffix="건"
                    valueColor="success"
                />

                <StatCard
                    title="수여 기관"
                    value={stats.uniqueOrgs}
                    suffix="개"
                />
            </StatsGrid>

            <FilterSection>
                <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="상훈명, 수여기관, 번호로 검색..."
                />
            </FilterSection>

            <TableContainer
                title="상훈 목록"
                resultCount={filteredAwards.length}
            >
                <Table
                    columns={columns}
                    data={filteredAwards}
                    pagination
                    pageSize={10}
                    defaultSortKey="award_date"
                    defaultSortOrder="desc"
                    stickyHeader
                    maxHeight="600px"
                    onRowClick={(award) => handleEdit(award)}
                    actionsColumnWidth="80px"
                    actions={(award) => (
                        <Button
                            size="small"
                            variant="danger-outline"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(award.id);
                            }}
                        >
                            삭제
                        </Button>
                    )}
                />
            </TableContainer>

            {/* 상훈 등록/수정 모달 */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title={editingAward ? '상훈 수정' : '상훈 등록'}
                size="large"
                footer={
                    <>
                        <Button
                            variant="outline"
                            onClick={() => setShowCreateModal(false)}
                            disabled={uploading}
                        >
                            취소
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSave}
                            loading={uploading}
                            disabled={
                                !awardForm.award_name ||
                                !awardForm.issuing_organization ||
                                !awardForm.award_date
                            }
                        >
                            {editingAward ? '수정' : '등록'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Input
                        label="상훈명"
                        required
                        type="text"
                        value={awardForm.award_name}
                        onChange={(e) => setAwardForm({...awardForm, award_name: e.target.value})}
                        placeholder="상훈명을 입력하세요"
                    />
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Input
                            label="수여기관"
                            required
                            type="text"
                            value={awardForm.issuing_organization}
                            onChange={(e) => setAwardForm({...awardForm, issuing_organization: e.target.value})}
                            placeholder="예: 국토교통부"
                        />
                        <Input
                            label="번호"
                            type="text"
                            value={awardForm.award_number}
                            onChange={(e) => setAwardForm({...awardForm, award_number: e.target.value})}
                            placeholder="예: 2024-001"
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Input
                            label="수여일"
                            required
                            type="date"
                            value={awardForm.award_date}
                            onChange={(e) => setAwardForm({...awardForm, award_date: e.target.value})}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium text-neutral-700">사진</span>
                        <ImageUploader
                            images={awardForm.photo_images}
                            onChange={handleImagesChange}
                            maxImages={1}
                            multiple={false}
                            accept="image/*"
                        />
                        <p className="text-xs text-muted-foreground">
                            상장, 트로피 등의 사진 (JPG, PNG 등 이미지 파일)
                        </p>
                    </div>
                </div>
            </Modal>
        </PageContainer>
    );
};

export default AwardManagementPage;
